/**
 * Timestamps printed on lab report documents must read in the lab's local
 * timezone no matter where the report is rendered — a pathologist's browser
 * or the backend's headless-Chrome renderer, which runs in UTC. Formatting
 * with the runtime's local timezone is what put UTC clock readings on signed
 * reports, so every report-facing formatter must pin this zone explicitly.
 *
 * Must match the backend's business timezone (`getBusinessTimezone()` in
 * backend/src/utils/timezone.ts, default America/New_York).
 */
export const LAB_TIME_ZONE = "America/New_York";

const ISO_DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_ONLY_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;
const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

type DateInput = string | Date;

/**
 * Date-only values ("2026-08-03" or the backend's pre-formatted "08/03/2026")
 * carry no time or timezone. They must be displayed literally — running them
 * through Date parsing would anchor them to UTC midnight and shift the
 * calendar day for any renderer west of UTC.
 */
function asDateOnlyDisplay(value: DateInput): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  const isoMatch = ISO_DATE_ONLY_PATTERN.exec(trimmed);
  if (isoMatch) return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
  if (US_DATE_ONLY_PATTERN.test(trimmed)) return trimmed;
  return null;
}

/** MM/DD/YYYY in the lab's timezone (date-only inputs pass through literally). */
export function formatLabCalendarDate(value: DateInput): string {
  const dateOnly = asDateOnlyDisplay(value);
  if (dateOnly) return dateOnly;
  return new Date(value).toLocaleDateString("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function formatLabShortDate(value: DateInput): string {
  const dateOnly = asDateOnlyDisplay(value);
  if (dateOnly) {
    const [month, day, year] = dateOnly.split("/");
    return `${SHORT_MONTH_NAMES[Number(month) - 1]} ${Number(day)}, ${year}`;
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

export function formatLabDateTime(value: DateInput): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

/** HH:mm (24-hour) in the lab's timezone (date-only inputs render as midnight). */
export function formatLabClockTime24(value: DateInput): string {
  if (asDateOnlyDisplay(value)) return "00:00";
  return new Date(value).toLocaleTimeString("en-US", {
    timeZone: LAB_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}
